import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Container, Divider, Paper, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useLocation, useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { FormikTextField } from '../components/FormikTextField';
import { FormikSubmitButton } from '../components/FormikSubmitButton';
import { useMfaSetupMutation, useMfaVerifyMutation } from '../services/authApi';
import { toDataURL } from 'qrcode';
import { useDispatch } from 'react-redux';
import { setUser } from '@/store/userSlice';

const validationSchema = yup.object({
  token: yup
    .string()
    .matches(/^\d{6}$/g, 'Enter the 6-digit code')
    .required('Code is required'),
});

export default function MFASetup() {
  const { enqueueSnackbar } = useSnackbar();
  const [mfaSetup] = useMfaSetupMutation();
  const [mfaVerify] = useMfaVerifyMutation();
  const [secret, setSecret] = useState<string | undefined>();
  const [qr, setQr] = useState<string | undefined>();
  const [setupToken, setSetupToken] = useState<string | undefined>();
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation() as any;
  const from = (location.state?.from as string) || '/dashboard';
  const preAuthToken = (location.state?.preAuthToken as string) || undefined;
  const requireSetup = !!location.state?.requireSetup;

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await mfaSetup(preAuthToken ? { preAuthToken } : undefined).unwrap();
        const url = res.otpauthUrl;
        setSecret(res.secret);
        setSetupToken(res.setupToken);
        if (url) {
          const dataUrl = await toDataURL(url, { margin: 1, width: 240 });
          setQr(dataUrl);
        }
      } catch (error: any) {
        if (error?.status === 401) {
          enqueueSnackbar('Session expired. Please log in again.', { variant: 'warning' });
          navigate('/login', { replace: true });
          return;
        }
        const errorMessage = error.data?.message || 'Failed to initialize 2FA setup';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canVerify = useMemo(() => !!setupToken, [setupToken]);

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Stack spacing={2}>
            <Typography component="h1" variant="h5">
              Set up Two-Factor Authentication (TOTP)
            </Typography>
            <Typography variant="body2">
              Scan the QR code below with your authenticator app (Google Authenticator, Authy, etc.).
              If you cannot scan the QR, you can enter the secret manually.
            </Typography>

            {loading ? (
              <Typography variant="body2">Loading setup info...</Typography>
            ) : (
              <>
                {qr ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <img src={qr} alt="TOTP QR Code" style={{ width: 240, height: 240 }} />
                  </Box>
                ) : null}
                {secret ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ userSelect: 'all' }}>
                      Secret: {secret}
                    </Typography>
                  </Box>
                ) : null}

                <Divider />

                <Typography variant="body2">
                  After adding the account in your authenticator app, enter the 6-digit code to verify.
                </Typography>

                <Formik
                  initialValues={{ token: '' }}
                  validationSchema={validationSchema}
                  onSubmit={async (values) => {
                    try {
                      if (!canVerify) {
                        enqueueSnackbar('Setup not ready. Please try again.', { variant: 'warning' });
                        return;
                      }
                      const response = await mfaVerify(
                        preAuthToken
                          ? { setupToken: setupToken!, token: values.token, preAuthToken }
                          : { setupToken: setupToken!, token: values.token }
                      ).unwrap();
                      const userData = {
                        userId: response.userId || null,
                        name: response.name || null,
                        email: response.email || null,
                        role: response.role || null,
                        jwtToken: response.jwtToken || null,
                        amcId: (response as any).amcId ?? null,
                      };
                      dispatch(setUser(userData));
                      enqueueSnackbar('Two-factor authentication enabled!', { variant: 'success' });
                      navigate('/dashboard', { replace: true });
                    } catch (error: any) {
                      const errorMessage = error.data?.message || 'Verification failed';
                      enqueueSnackbar(errorMessage, { variant: 'error' });
                    }
                  }}
                >
                  <Form style={{ width: '100%', marginTop: '8px' }}>
                    <FormikTextField
                      name="token"
                      margin="normal"
                      label="6-digit code"
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
                    />
                    <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 1 }}>
                      {!requireSetup && (
                        <Button variant="text" onClick={() => navigate(from, { replace: true })}>
                          Skip for now
                        </Button>
                      )}
                      <FormikSubmitButton variant="contained">Verify & Enable</FormikSubmitButton>
                    </Stack>
                  </Form>
                </Formik>
              </>
            )}
          </Stack>
        </Paper>
      </Box>
    </Container>
  );
}
